import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import queryString from 'query-string';
import { getFormVersion } from 'api/editor';
import { initClientForm } from 'api/client';
import { setI18nLanguage } from 'locale/i18n';
import { FEVER_API_ENDPOINT } from 'config/envVariable';
import { refreshFormData } from 'redux/modules/formData';
import items from 'localData/dataPromo.json';

const useInitForm = ({ formId, sponsorId }) => {
    const dispatch = useDispatch();
    const [httpStatus, setHttpStatus] = useState(null);
    const [initData, setInitData] = useState(null);
    const [initTrack, setInitTrack] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const setFailed = useCallback(status => {
        setInitData(false);
        setIsLoading(false);
        if (status) {
            setHttpStatus(status);
        }
    }, []);

    const fetchData = useCallback(
        async id => {
            setIsLoading(true);
            try {
                const formVersionResp = await getFormVersion(id || formId);
                if (formVersionResp.status === 404) {
                    return setFailed(404);
                }

                const {
                    payload: {
                        items: { version: formVersion }
                    }
                } = formVersionResp;

                const formInfo =
                    FEVER_API_ENDPOINT === 'localhost'
                        ? items
                        : await initClientForm(id || formId, formVersion);

                if (formInfo.code === 200) {
                    const { track, ...restData } = formInfo.items;
                    const flagInitState = {
                        selectPageId: formInfo.items.pages.find(page => page.type === 1).id,
                        formVersion
                    };
                    const data = {
                        ...restData,
                        flag: flagInitState
                    };

                    const {
                        form: { promotionId },
                        settings: {
                            form: { lang }
                        }
                    } = formInfo.items;

                    let clientLang = lang;
                    document.documentElement.setAttribute('lang', clientLang);

                    if (promotionId) {
                        const { promo_lang } = queryString.parse(window.location.search);
                        if (promo_lang) {
                            clientLang = promo_lang;
                            formInfo.items.settings.form.lang = promo_lang;
                        }
                    }

                    setI18nLanguage({ lang: clientLang });
                    setInitData(data);
                    setInitTrack(track);
                    setIsLoading(false);
                    dispatch(refreshFormData(data));
                } else {
                    const { code, message } = formInfo;
                    if (code === 404 && message === '3-1000-0000-31') {
                        setFailed(202);
                    } else if (code === 422 && message === '3-0001-0301-31') {
                        setFailed(422);
                    } else {
                        setFailed(404);
                    }
                }
            } catch (error) {
                console.error('Error', error);
                setFailed(404);
            }
        },
        [dispatch, setFailed, formId]
    );

    const refetch = useCallback(
        id => {
            setIsLoading(true);
            fetchData(id);
        },
        [fetchData]
    );

    useEffect(() => {
        if (!formId) return;
        if (!sponsorId) return;
        if (initData) return;
        fetchData();
    }, [formId, initData, fetchData, sponsorId]);

    return {
        httpStatus,
        initData,
        initTrack,
        isLoading,
        refetch
    };
};

export default useInitForm;
